import { setSignUpStatus, signUp } from "actions/user";
import SageButton from "kit/SageButton";
import SageErrorAlert from "kit/SageErrorAlert";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import signUpStatusSelector from "selectors/signUpStatus";

const SignUpBody: React.FC = () => {
    const dispatch = useDispatch();
    const signUpStatus = useSelector(signUpStatusSelector);
    const isLoading = React.useMemo(() => signUpStatus === "loading", [
        signUpStatus,
    ]);
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [confirmPassword, setConfirmPassword] = React.useState("");

    const errorText = React.useMemo(() => {
        if (signUpStatus === "error") {
            return "An error occurred. Please try logging in again";
        } else if (signUpStatus === "passwords_dont_match") {
            return "Your passwords do not match";
        } else if (signUpStatus === "email_exists") {
            return "This email is already taken. Please try logging in.";
        }
        return null;
    }, [signUpStatus]);
    const signUpFormSubmitted = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (password !== confirmPassword) {
                dispatch(setSignUpStatus("passwords_dont_match"));
                return;
            }
            dispatch(signUp(email, password));
        },
        [dispatch, email, password, confirmPassword]
    );
    const onDismissError = React.useCallback(() => {
        dispatch(setSignUpStatus("none"));
    }, [dispatch]);
    const onEmailChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
        },
        [setEmail]
    );
    const onPasswordChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(e.target.value);
        },
        [setPassword]
    );
    const onConfirmPasswordChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmPassword(e.target.value);
        },
        [setConfirmPassword]
    );

    return (
        <>
            <form onSubmit={signUpFormSubmitted} id="sign-up-form">
                <div className="form-group">
                    <label htmlFor="emailInput">Email Address</label>
                    <input
                        autoComplete="email"
                        value={email}
                        onChange={onEmailChange}
                        disabled={isLoading}
                        type="email"
                        className="form-control"
                        id="emailInput"
                        aria-describedby="emailHelp"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="passwordInput">Password</label>
                    <input
                        value={password}
                        onChange={onPasswordChange}
                        disabled={isLoading}
                        type="password"
                        autoComplete="new-password"
                        className="form-control"
                        id="passwordInput"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="confirmPasswordInput">
                        Confirm Password
                    </label>
                    <input
                        value={confirmPassword}
                        onChange={onConfirmPasswordChange}
                        disabled={isLoading}
                        type="password"
                        autoComplete="new-password"
                        className="form-control"
                        id="confirmPasswordInput"
                    />
                </div>
                <SageButton loading={isLoading} text="Sign Up" type="submit" />
            </form>
            {errorText && (
                <SageErrorAlert onDismiss={onDismissError} text={errorText} />
            )}
        </>
    );
};

export default SignUpBody;
